// src/components/TokenCreationForm.tsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  Rocket, 
  Info, 
  ExternalLink, 
  AlertCircle, 
  Shield,
  Zap,
  Package,
  Check,
  CheckCircle,
  Lock,
  Unlock,
  DollarSign
} from 'lucide-react';
import { useWallet } from '../contexts/WalletContext';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';
import { SOLANA_RPC_ENDPOINT } from '../utils/constants';
import { Image as ImageIcon, Loader2, X } from "lucide-react";
import { apiEndpoints } from '../services/api.service';
import revshareService from '../services/revshare.service';
import { bundlerService } from '../services/frontend-bundler/bundler.service';
import Modal from './Modal'; // Import the Modal component you created earlier

interface TokenFormData {
  name: string;
  ticker: string;
  description: string;
  imageUrl: string;
  website: string;
  twitter: string;
  telegram: string;
  mode: 'rewards' | 'jackpot' | 'lottery' | 'none';
  visible: boolean;
}

interface LaunchSettings {
  slippage: number;
  priorityFee: number;
  useJitoBundle: boolean;
  jitoTip: number;
}

const TokenCreationForm: React.FC = () => {
  const { isConnected, publicKey } = useWallet();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<'form' | 'funding' | 'creating' | 'success'>('form');
  const [requestId, setRequestId] = useState<number | null>(null);
  const [fundingWallet, setFundingWallet] = useState<string>('');
  const [amountToFund, setAmountToFund] = useState<number>(0);
  const [createdTokenAddress, setCreatedTokenAddress] = useState<string>('');
  const [txHash, setTxHash] = useState<string>('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const MAX_BYTES = 5 * 1024 * 1024;
  
  const [formData, setFormData] = useState<TokenFormData>({
    name: '',
    ticker: '',
    description: '',
    imageUrl: '',
    website: '',
    twitter: '',
    telegram: '',
    mode: 'rewards',
    visible: true
  });

  const [referrerWallet, setReferrerWallet] = useState<string>('');
  const [initialBuyAmount, setInitialBuyAmount] = useState<number>(0);

  // Bundle state
  const [bundles, setBundles] = useState<any[]>([]);
  const [selectedBundle, setSelectedBundle] = useState<any>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [bundlePassword, setBundlePassword] = useState('');
  const [showBundlePasswordModal, setShowBundlePasswordModal] = useState(false);
  const [bundleBuyProgress, setBundleBuyProgress] = useState<{
    total: number;
    completed: number;
    successful: number;
    failed: number;
    status: string;
  }>({ total: 0, completed: 0, successful: 0, failed: 0, status: '' });

  // Advanced settings state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [launchSettings, setLaunchSettings] = useState<LaunchSettings>({
    slippage: 10,
    priorityFee: 0.0005,
    useJitoBundle: false,
    jitoTip: 0.01
  });

  // Load bundles when wallet connects
  useEffect(() => {
    if (isConnected && publicKey) {
      loadUserBundles();
    }
  }, [isConnected, publicKey]);

  const loadUserBundles = async () => {
    if (!publicKey) return;
    try {
      const userBundles = await bundlerService.getUserBundles(publicKey.toString());
      setBundles(userBundles);
      
      // Check if there's an active unlocked bundle
      const currentBundle = bundlerService.getCurrentBundle();
      if (currentBundle && !bundlerService.getIsLocked()) {
        setSelectedBundle(currentBundle);
        setIsUnlocked(true);
      }
    } catch (error) {
      console.error('Failed to load bundles:', error);
    }
  };

  const unlockSelectedBundle = async () => {
    if (!selectedBundle || !bundlePassword) return;
    
    try {
      const unlockedBundle = await bundlerService.unlockBundle(
        selectedBundle.id, 
        bundlePassword
      );
      setIsUnlocked(true);
      setShowBundlePasswordModal(false);
      setBundlePassword('');
      console.log(`✅ Bundle "${unlockedBundle.name}" unlocked successfully!`);
    } catch (error: any) {
      alert(`Failed to unlock bundle: ${error.message}`);
    }
  };

  const handleUploadFile = async (file: File) => {
    if (!/^image\/(png|jpe?g|gif|webp)$/i.test(file.type)) {
      alert('Only PNG, JPG, GIF, WEBP are allowed');
      return;
    }
    if (file.size > MAX_BYTES) {
      alert(`File too large (max ${MAX_BYTES / 1024 / 1024}MB)`);
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(
        `${apiEndpoints.uploadLogo}?folder=revshare-tokens&prefix=revshare`,
        {
          method: 'POST',
          body: formData
        }
      );
      
      if (!response.ok) {
        throw new Error(`Upload failed (${response.status})`);
      }
      
      const data = await response.json();
      
      // Check for the URL in the response (could be secure_url or url)
      const imageUrl = data.secure_url || data.url || data.imageUrl;
      
      if (!imageUrl) {
        throw new Error('No image URL in response');
      }
      
      setFormData(prev => ({ ...prev, imageUrl }));
      
      console.log('✅ Image uploaded successfully:', imageUrl);
      
    } catch (error: any) {
      console.error('Upload error:', error);
      
      // Fallback to base64 encoding if upload fails
      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          setFormData(prev => ({ ...prev, imageUrl: base64 }));
          console.log('📦 Using base64 fallback');
        };
        reader.readAsDataURL(file);
      } catch (fallbackError) {
        alert('Failed to upload image. Please try again.');
      }
    } finally {
      setUploadingLogo(false);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.name || formData.name.length > 50) {
      alert('Token name is required and must be less than 50 characters');
      return false;
    }
    if (!formData.ticker || formData.ticker.length > 10) {
      alert('Token ticker is required and must be less than 10 characters');
      return false;
    }
    if (!formData.description || formData.description.length > 500) {
      alert('Description is required and must be less than 500 characters');
      return false;
    }
    if (!formData.imageUrl) {
      alert('Token logo is required');
      return false;
    }
    if (!isConnected || !publicKey) {
      alert('Please connect your wallet first');
      return false;
    }
    return true;
  };

  const fundCreationWallet = async (
    fundingAddress: string, 
    amount: number
  ): Promise<boolean> => {
    try {
      if (!publicKey) {
        throw new Error('Wallet not connected');
      }

      console.log(`💰 Funding wallet ${fundingAddress} with ${amount} SOL...`);

      const connection = new Connection(SOLANA_RPC_ENDPOINT, 'confirmed');
      const fromPubkey = new PublicKey(publicKey);
      const toPubkey = new PublicKey(fundingAddress);

      const transaction = new Transaction().add(
        SystemProgram.transfer({
          fromPubkey,
          toPubkey,
          lamports: amount * LAMPORTS_PER_SOL
        })
      );

      const { blockhash } = await connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = fromPubkey;

      const provider = (window as any).solana;
      if (!provider?.isPhantom) {
        throw new Error('Phantom wallet not found');
      }

      const signedTransaction = await provider.signTransaction(transaction);
      const signature = await connection.sendRawTransaction(signedTransaction.serialize());
      
      await connection.confirmTransaction(signature, 'confirmed');
      
      console.log('✅ Funding transaction confirmed:', signature);
      setTxHash(signature);
      
      // Wait for API to detect the funding
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      return true;
    } catch (error: any) {
      console.error('❌ Failed to fund wallet:', error);
      alert(`Failed to fund wallet: ${error.message}`);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setBundleBuyProgress({
      total: 0,
      completed: 0,
      successful: 0,
      failed: 0,
      status: 'Preparing token creation...'
    });
    
    try {
      // Check for Jito bundle (not implemented)
      if (launchSettings.useJitoBundle) {
        throw new Error(
          'Jito bundle flow is not implemented yet. Please disable MEV protection or implement Jito submission.'
        );
      }

      // Step 1: Prepare token creation
      setStep('funding');
      
      // Calculate the actual initial buy amount
      // If bundle is selected and unlocked, we don't buy from main wallet
      const mainWalletBuyAmount = selectedBundle && isUnlocked ? 0 : initialBuyAmount;
      
      const prepareResult = await revshareService.prepareTokenCreation(mainWalletBuyAmount);
      
      if (!prepareResult.success) {
        throw new Error(prepareResult.error || 'Failed to prepare token creation');
      }

      if (!prepareResult.request_id || !prepareResult.funding_wallet || !prepareResult.amount_to_fund) {
        throw new Error('Invalid preparation response: missing required fields');
      }

      setRequestId(prepareResult.request_id);
      setFundingWallet(prepareResult.funding_wallet);
      setAmountToFund(prepareResult.amount_to_fund);

      // Step 2: Fund the creation wallet
      const fundingSuccess = await fundCreationWallet(
        prepareResult.funding_wallet,
        prepareResult.amount_to_fund
      );

      if (!fundingSuccess) {
        throw new Error('Failed to fund creation wallet');
      }

      // Step 3: Create the token
      setStep('creating');
      setBundleBuyProgress(prev => ({ ...prev, status: 'Creating token on Solana...' }));
      
      // Build the token creation data
      const createTokenData = {
        request_id: prepareResult.request_id,
        name: formData.name,
        ticker: formData.ticker,
        description: formData.description,
        imageUrl: formData.imageUrl,
        developerWallet: publicKey!.toString(),
        ref: referrerWallet || undefined,
        website: formData.website || undefined,
        twitter: formData.twitter || undefined,
        telegram: formData.telegram || undefined,
        visible: formData.visible ? 0 : 1, // 0=visible, 1=hidden
        mode: revshareService.getModeValue(formData.mode)
      };

      const createResult = await revshareService.createRevShareToken(createTokenData);

      if (!createResult.success) {
        throw new Error(createResult.error || 'Failed to create token');
      }

      const mintAddress = createResult.mintAddress || '';
      
      // Track revenue for referral system (if applicable)
      if (mainWalletBuyAmount > 0) {
        await revshareService.trackRevenue(mainWalletBuyAmount, 'token_launch', {
          tokenName: formData.name,
          ticker: formData.ticker,
          mintAddress: mintAddress
        });
      }

      // Step 4: Execute bundle purchases if a bundle is selected and unlocked
      if (selectedBundle && isUnlocked && mintAddress && initialBuyAmount > 0) {
        console.log('🎯 Starting bundle buys for token:', mintAddress);

        setBundleBuyProgress({
          total: selectedBundle.wallets.length,
          completed: 0,
          successful: 0,
          failed: 0,
          status: 'Executing bundle purchases...',
        });

        const bundleResult = await bundlerService.executeBundlePurchase(
          mintAddress,
          initialBuyAmount,
          launchSettings.slippage,
          (progress, status) => {
            const match = status.match(/(\d+) success, (\d+) failed/);
            if (match) {
              setBundleBuyProgress({
                total: selectedBundle.wallets.length,
                completed: parseInt(match[1]) + parseInt(match[2]),
                successful: parseInt(match[1]),
                failed: parseInt(match[2]),
                status,
              });
            } else {
              setBundleBuyProgress((prev) => ({ ...prev, status }));
            }
          }
        );

        if (bundleResult.success) {
          console.log(`✅ Bundle buys complete: ${bundleResult.successful} successful`);
          
          setBundleBuyProgress((prev) => ({
            ...prev,
            completed: prev.total,
            successful: bundleResult.successful,
            failed: bundleResult.failed,
            status: `Bundle purchases complete! ${bundleResult.successful}/${prev.total} successful`,
          }));
        } else {
          console.error('Bundle buy failed:', bundleResult.error);
        }
      }

      // Success!
      setCreatedTokenAddress(mintAddress);
      setStep('success');
      
      // Check if user was referred
      const referrer = sessionStorage.getItem('referrer');
      if (referrer) {
        console.log('💸 Referrer will receive rewards from this token creation');
      }

    } catch (error: any) {
      console.error('Failed to create token:', error);
      alert(`Failed to create token: ${error.message}`);
      setStep('form');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      ticker: '',
      description: '',
      imageUrl: '',
      website: '',
      twitter: '',
      telegram: '',
      mode: 'rewards',
      visible: true
    });
    setInitialBuyAmount(0);
    setStep('form');
    setRequestId(null);
    setFundingWallet('');
    setAmountToFund(0);
    setCreatedTokenAddress('');
    setTxHash('');
    setReferrerWallet('');
    setBundleBuyProgress({ total: 0, completed: 0, successful: 0, failed: 0, status: '' });
    setSelectedBundle(null);
    setIsUnlocked(false);
  };

  // Success screen
  if (step === 'success') {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Rocket className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Token Created Successfully!</h2>
          <p className="text-gray-400 mb-6">Your RevShare token has been deployed on Solana</p>
          
          <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
            <div className="text-sm text-gray-400 mb-1">Token Address</div>
            <div className="font-mono text-sm break-all">{createdTokenAddress}</div>
          </div>

          {txHash && (
            <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
              <div className="text-sm text-gray-400 mb-1">Transaction Hash</div>
              <div className="font-mono text-xs break-all">{txHash}</div>
            </div>
          )}

          {bundleBuyProgress.total > 0 && (
            <div className="bg-purple-900/20 border border-purple-700/30 rounded-lg p-4 mb-6">
              <div className="text-sm font-medium text-purple-400 mb-2">Bundle Purchase Results</div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="bg-gray-900/50 rounded p-2">
                  <div className="text-xs text-gray-500">Total</div>
                  <div className="font-semibold">{bundleBuyProgress.total}</div>
                </div>
                <div className="bg-green-900/30 rounded p-2">
                  <div className="text-xs text-gray-500">Successful</div>
                  <div className="font-semibold text-green-400">{bundleBuyProgress.successful}</div>
                </div>
                <div className="bg-red-900/30 rounded p-2">
                  <div className="text-xs text-gray-500">Failed</div>
                  <div className="font-semibold text-red-400">{bundleBuyProgress.failed}</div>
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-2">
            <a
              href={`https://solscan.io/token/${createdTokenAddress}${SOLANA_RPC_ENDPOINT.includes('devnet') ? '?cluster=devnet' : ''}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
            >
              <ExternalLink className="w-4 h-4" />
              <span>View on Solscan</span>
            </a>
            <button
              onClick={resetForm}
              className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
            >
              Create Another Token
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Funding/Creating screen
  if (step === 'funding' || step === 'creating') {
    return (
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-purple-400" />
          <h2 className="text-xl font-bold mb-2">
            {step === 'funding' ? 'Processing Payment...' : 'Creating Your Token...'}
          </h2>
          
          {step === 'funding' && fundingWallet && (
            <div className="mt-4 p-4 bg-gray-900/50 rounded-lg">
              <div className="text-sm text-gray-400 mb-2">Funding Wallet</div>
              <div className="font-mono text-xs break-all mb-2">{fundingWallet}</div>
              <div className="text-sm text-gray-400">Total Amount: {amountToFund} SOL</div>
              {initialBuyAmount > 0 && !selectedBundle && (
                <div className="text-xs text-gray-500 mt-1">
                  (Includes {initialBuyAmount} SOL for initial buy)
                </div>
              )}
            </div>
          )}
          
          {step === 'creating' && bundleBuyProgress.total > 0 && (
            <div className="mt-6 max-w-md mx-auto">
              <div className="text-sm font-medium mb-2">Bundle Buy Progress</div>
              <div className="w-full bg-gray-800 rounded-full h-2 mb-2">
                <div 
                  className="bg-gradient-to-r from-purple-600 to-pink-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(bundleBuyProgress.completed / bundleBuyProgress.total) * 100}%` }}
                />
              </div>
              <div className="text-xs text-gray-400">
                {bundleBuyProgress.completed}/{bundleBuyProgress.total} wallets • 
                <span className="text-green-400"> {bundleBuyProgress.successful} successful</span> • 
                <span className="text-red-400"> {bundleBuyProgress.failed} failed</span>
              </div>
            </div>
          )}
          
          <p className="text-gray-400 mt-4">
            {bundleBuyProgress.status || (
              step === 'funding' 
                ? 'Please approve the transaction in your wallet...'
                : 'Deploying your token on Solana blockchain...'
            )}
          </p>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700">
      <h2 className="text-2xl font-bold mb-2 flex items-center">
        <Rocket className="w-6 h-6 mr-2 text-purple-400" />
        Create RevShare Token
      </h2>
      <p className="text-sm text-gray-400 mb-6">
        Launch a token with automated revenue sharing on Solana
      </p>

      {/* Important Notice */}
      <div className="mb-6 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
        <div className="flex items-start space-x-2">
          <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-400">
            <div className="font-medium mb-1">Important Information</div>
            <ul className="text-xs text-gray-400 space-y-1">
              <li>• All tokens are created with a fixed supply of 1 billion tokens</li>
              <li>• Transfer fees and distribution percentages are preset by RevShare</li>
              <li>• Token authorities are managed automatically by the platform</li>
              <li>• Distribution modes determine how collected fees are shared</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="space-y-4">
        {/* Token Name */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Name <span className="text-red-500">*</span>
            <span className="text-gray-500 text-xs ml-2">(max 50 chars)</span>
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value.slice(0, 50) })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
            placeholder="My Awesome Token"
            maxLength={50}
          />
          <div className="text-xs text-gray-500 mt-1">{formData.name.length}/50</div>
        </div>
        
        {/* Token Ticker */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Ticker <span className="text-red-500">*</span>
            <span className="text-gray-500 text-xs ml-2">(max 10 chars)</span>
          </label>
          <input
            type="text"
            value={formData.ticker}
            onChange={(e) => setFormData({ ...formData, ticker: e.target.value.slice(0, 10).toUpperCase() })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
            placeholder="TOKEN"
            maxLength={10}
          />
          <div className="text-xs text-gray-500 mt-1">{formData.ticker.length}/10</div>
        </div>
        
        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Description <span className="text-red-500">*</span>
            <span className="text-gray-500 text-xs ml-2">(max 500 chars)</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value.slice(0, 500) })}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
            placeholder="Describe your token and its purpose..."
            rows={3}
            maxLength={500}
          />
          <div className="text-xs text-gray-500 mt-1">{formData.description.length}/500</div>
        </div>
        
        {/* Token Logo */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Token Logo <span className="text-red-500">*</span>
          </label>
          <div
            onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={async (e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (file) await handleUploadFile(file);
            }}
            onClick={() => fileInputRef.current?.click()}
            className={`w-full min-h-[140px] rounded-xl border-2 border-dashed transition-all cursor-pointer ${
              isDragging 
                ? 'border-purple-400 bg-purple-500/5' 
                : 'border-gray-700 bg-gray-900/30 hover:border-purple-500/50'
            } flex items-center justify-center p-6`}
          >
            {!formData.imageUrl && !uploadingLogo && (
              <div className="text-center">
                <ImageIcon className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-sm font-medium mb-1">
                  Drop your logo here or click to upload
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG, GIF or WEBP (max 5MB)
                </p>
              </div>
            )}

            {uploadingLogo && (
              <div className="flex items-center space-x-3">
                <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                <span className="text-sm">Uploading to IPFS...</span>
              </div>
            )}

            {formData.imageUrl && !uploadingLogo && (
              <div className="flex items-center gap-4">
                <img
                  src={formData.imageUrl}
                  alt="Token Logo"
                  className="w-20 h-20 rounded-lg object-cover border-2 border-purple-500/30"
                />
                <div className="text-left">
                  <p className="text-sm text-gray-400 mb-2">Logo uploaded</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData(prev => ({ ...prev, imageUrl: '' }));
                      }}
                      className="px-3 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-1"
                    >
                      <X className="w-3 h-3" /> Remove
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors"
                    >
                      Replace
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (file) await handleUploadFile(file);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
        </div>

        {/* Distribution Mode */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Distribution Mode <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <button
              type="button"
              onClick={() => setFormData({ ...formData, mode: 'rewards' })}
              className={`px-3 py-3 rounded-lg border-2 transition-all ${
                formData.mode === 'rewards' 
                  ? 'bg-purple-600 border-purple-500 shadow-lg shadow-purple-500/30' 
                  : 'bg-gray-900/50 border-gray-700 hover:border-purple-600/50 hover:bg-gray-800/50'
              }`}
            >
              <div className="font-semibold text-sm">Rewards</div>
              <div className="text-xs opacity-80 mt-1">Fee sharing</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, mode: 'jackpot' })}
              className={`px-3 py-3 rounded-lg border-2 transition-all ${
                formData.mode === 'jackpot' 
                  ? 'bg-purple-600 border-purple-500 shadow-lg shadow-purple-500/30' 
                  : 'bg-gray-900/50 border-gray-700 hover:border-purple-600/50 hover:bg-gray-800/50'
              }`}
            >
              <div className="font-semibold text-sm">Jackpot</div>
              <div className="text-xs opacity-80 mt-1">Prize pool</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, mode: 'lottery' })}
              className={`px-3 py-3 rounded-lg border-2 transition-all ${
                formData.mode === 'lottery' 
                  ? 'bg-purple-600 border-purple-500 shadow-lg shadow-purple-500/30' 
                  : 'bg-gray-900/50 border-gray-700 hover:border-purple-600/50 hover:bg-gray-800/50'
              }`}
            >
              <div className="font-semibold text-sm">Lottery</div>
              <div className="text-xs opacity-80 mt-1">Random wins</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData({ ...formData, mode: 'none' })}
              className={`px-3 py-3 rounded-lg border-2 transition-all ${
                formData.mode === 'none' 
                  ? 'bg-purple-600 border-purple-500 shadow-lg shadow-purple-500/30' 
                  : 'bg-gray-900/50 border-gray-700 hover:border-purple-600/50 hover:bg-gray-800/50'
              }`}
            >
              <div className="font-semibold text-sm">None</div>
              <div className="text-xs opacity-80 mt-1">No rewards</div>
            </button>
          </div>
          <div className="mt-2 text-xs text-gray-400">
            {formData.mode === 'rewards' && "Fees are automatically shared between the developer wallet and token holders"}
            {formData.mode === 'jackpot' && "All fees accumulate in a prize pool for jackpot winners"}
            {formData.mode === 'lottery' && "Fees are distributed randomly to lucky token holders"}
            {formData.mode === 'none' && "No fee distribution mechanism"}
          </div>
        </div>

        {/* Social Links (Optional) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Website <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
              placeholder="https://..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Twitter <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.twitter}
              onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
              placeholder="@username"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">
              Telegram <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.telegram}
              onChange={(e) => setFormData({ ...formData, telegram: e.target.value })}
              className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
              placeholder="t.me/..."
            />
          </div>
        </div>

        {/* Visibility */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="visible"
            checked={formData.visible}
            onChange={(e) => setFormData({ ...formData, visible: e.target.checked })}
            className="w-4 h-4 text-purple-600 bg-gray-900 border-gray-700 rounded focus:ring-purple-500"
          />
          <label htmlFor="visible" className="text-sm">
            List token publicly on RevShare.dev
          </label>
        </div>

        {/* Referrer Wallet (Optional) */}
        <div>
          <label className="block text-sm font-medium mb-2">
            Referrer Wallet <span className="text-gray-500 text-xs">(optional)</span>
          </label>
          <input
            type="text"
            value={referrerWallet}
            onChange={(e) => setReferrerWallet(e.target.value)}
            className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
            placeholder="Solana wallet address of your referrer"
          />
          <div className="text-xs text-gray-500 mt-1">
            If someone referred you to RevShare, enter their wallet address
          </div>
        </div>

        {/* Advanced Settings Section */}
        <div className="border-t border-gray-700 pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center justify-between w-full py-2 text-left"
          >
            <span className="font-medium flex items-center space-x-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span>Advanced Settings</span>
            </span>
            <span className="text-gray-400">
              {showAdvanced ? '−' : '+'}
            </span>
          </button>
          
          {showAdvanced && (
            <div className="mt-4 space-y-4">
              {/* Bundle Selection */}
              <div>
                <h4 className="font-medium flex items-center space-x-2 mb-3">
                  <Package className="w-4 h-4 text-purple-400" />
                  <span>Bundle Auto-Buy</span>
                  {selectedBundle && (
                    <span className={`px-2 py-0.5 text-xs rounded-full ${
                      isUnlocked 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>
                      {isUnlocked ? '🔓 Unlocked' : '🔒 Locked'}
                    </span>
                  )}
                </h4>
                
                {bundles.length > 0 ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {bundles.map((bundle) => {
                        const eligibleWallets = bundle.wallets.filter(
                          (w: any) => w.balance >= (initialBuyAmount + 0.002)
                        );
                        
                        return (
                          <button
                            key={bundle.id}
                            type="button"
                            onClick={() => {
                              setSelectedBundle(bundle);
                              setIsUnlocked(false);
                            }}
                            className={`p-3 rounded-lg border text-left transition-all ${
                              selectedBundle?.id === bundle.id
                                ? 'border-purple-500 bg-purple-500/10'
                                : 'border-gray-700 bg-gray-900/30 hover:border-gray-600'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium">{bundle.name}</div>
                              {selectedBundle?.id === bundle.id && (
                                <Check className="w-4 h-4 text-purple-400" />
                              )}
                            </div>
                            <div className="text-xs text-gray-400 space-y-1">
                              <div>{bundle.wallets.length} wallets • {bundle.totalBalance.toFixed(3)} SOL</div>
                              <div className={eligibleWallets.length > 0 ? 'text-green-400' : 'text-red-400'}>
                                {eligibleWallets.length} can afford {initialBuyAmount} SOL + fees
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    
                    {selectedBundle && !isUnlocked && (
                      <button
                        type="button"
                        onClick={() => setShowBundlePasswordModal(true)}
                        className="w-full px-4 py-2 bg-purple-600/20 text-purple-400 rounded-lg hover:bg-purple-600/30 transition-all"
                      >
                        Unlock Bundle for Auto-Buy
                      </button>
                    )}
                    
                    {selectedBundle && isUnlocked && (
                      <div className="p-3 bg-green-900/20 border border-green-700/30 rounded-lg">
                        <div className="flex items-start space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5" />
                          <div className="text-sm">
                            <div className="font-medium text-green-400">Bundle Ready</div>
                            <div className="text-xs text-gray-400 mt-1">
                              {selectedBundle.wallets.filter((w: any) => 
                                w.balance >= (initialBuyAmount + 0.002)
                              ).length} wallets will automatically buy after token creation
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-sm text-gray-400">
                    No bundles available. Create a bundle in Advanced Tools to enable auto-buying.
                  </div>
                )}
              </div>

              {/* Initial Buy Amount */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Buy Amount per Wallet (SOL)
                </label>
                <input
                  type="number"
                  value={initialBuyAmount}
                  onChange={(e) => setInitialBuyAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                  className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
                  placeholder="0"
                  min="0"
                  step="0.1"
                />
                <div className="text-xs text-gray-500 mt-1">
                  {selectedBundle && isUnlocked 
                    ? `Each wallet in bundle will buy this amount`
                    : `Amount to buy immediately after creation`}
                </div>
              </div>

              {/* Trading Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Slippage (%)
                  </label>
                  <input
                    type="number"
                    value={launchSettings.slippage}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      slippage: Number(e.target.value)
                    })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
                    min="1"
                    max="50"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Priority Fee (SOL)
                  </label>
                  <input
                    type="number"
                    value={launchSettings.priorityFee}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      priorityFee: Number(e.target.value)
                    })}
                    className="w-full px-4 py-2 bg-gray-900/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
                    min="0"
                    step="0.0001"
                  />
                </div>
              </div>

              {/* Jito Bundle Option */}
              <div className="bg-purple-900/10 border border-purple-700/30 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="jito-bundle"
                    checked={launchSettings.useJitoBundle}
                    onChange={(e) => setLaunchSettings({
                      ...launchSettings,
                      useJitoBundle: e.target.checked
                    })}
                    className="mt-1 w-4 h-4 text-purple-600 bg-gray-900 border-gray-700 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="jito-bundle" className="flex-1 cursor-pointer">
                    <div className="font-medium flex items-center space-x-2">
                      <Shield className="w-4 h-4 text-purple-400" />
                      <span>MEV Protection (Jito Bundle)</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Protect against sandwich attacks and ensure transaction success
                    </p>
                  </label>
                </div>
                
                {launchSettings.useJitoBundle && (
                  <div className="mt-3 ml-7">
                    <label className="block text-sm font-medium mb-2">
                      Jito Tip (SOL)
                    </label>
                    <input
                      type="number"
                      value={launchSettings.jitoTip}
                      onChange={(e) => setLaunchSettings({
                        ...launchSettings,
                        jitoTip: Number(e.target.value)
                      })}
                      className="w-full px-3 py-1.5 bg-gray-800/50 border border-gray-700 rounded-lg focus:outline-none focus:border-purple-500"
                      min="0.001"
                      step="0.001"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      Higher tips = higher priority
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cost Breakdown */}
        <div className="bg-gray-900/50 rounded-lg p-4 space-y-3">
          <div className="flex items-start space-x-2">
            <DollarSign className="w-4 h-4 text-purple-400 mt-0.5" />
            <div className="text-sm flex-1">
              <div className="font-medium text-purple-400 mb-2">Token Creation Cost</div>
              <div className="space-y-1 text-gray-400">
                <div className="flex justify-between">
                  <span>Base Cost:</span>
                  <span className="font-mono">0.05 SOL</span>
                </div>
                {selectedBundle && isUnlocked && initialBuyAmount > 0 ? (
                  <div className="flex justify-between">
                    <span>Bundle Buy ({selectedBundle.wallets.filter((w: any) => 
                      w.balance >= (initialBuyAmount + 0.002)
                    ).length} wallets):</span>
                    <span className="font-mono">
                      {(initialBuyAmount * selectedBundle.wallets.filter((w: any) => 
                        w.balance >= (initialBuyAmount + 0.002)
                      ).length).toFixed(3)} SOL
                    </span>
                  </div>
                ) : initialBuyAmount > 0 && (
                  <div className="flex justify-between">
                    <span>Initial Buy:</span>
                    <span className="font-mono">{initialBuyAmount} SOL</span>
                  </div>
                )}
                {launchSettings.useJitoBundle && (
                  <div className="flex justify-between">
                    <span>Jito Tip:</span>
                    <span className="font-mono">{launchSettings.jitoTip} SOL</span>
                  </div>
                )}
                <div className="border-t border-gray-700 pt-1 mt-1">
                  <div className="flex justify-between font-medium">
                    <span>Total from Main Wallet:</span>
                    <span className="font-mono text-purple-400">
                      {(
                        0.05 + 
                        (selectedBundle && isUnlocked ? 0 : initialBuyAmount) + 
                        (launchSettings.useJitoBundle ? launchSettings.jitoTip : 0)
                      ).toFixed(3)} SOL
                    </span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Exact amount will be calculated after preparation
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={loading || !isConnected}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <Rocket className="w-5 h-5" />
              <span>Create Token</span>
            </>
          )}
        </button>

        {!isConnected && (
          <p className="text-center text-sm text-yellow-400 mt-2">
            Please connect your wallet to create a token
          </p>
        )}
      </div>

      {/* Bundle Password Modal */}
      <Modal 
        isOpen={showBundlePasswordModal} 
        onClose={() => {
          setShowBundlePasswordModal(false);
          setBundlePassword('');
        }}
      >
        <h3 className="text-xl font-bold mb-4">Unlock Bundle</h3>
        <p className="text-sm text-gray-400 mb-4">
          Enter password to unlock "{selectedBundle?.name}" for auto-buying
        </p>
        
        <input
          type="password"
          value={bundlePassword}
          onChange={(e) => setBundlePassword(e.target.value)}
          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2 mb-4 focus:outline-none focus:border-purple-500"
          placeholder="Bundle password"
          onKeyPress={(e) => e.key === 'Enter' && unlockSelectedBundle()}
          autoFocus
        />
        
        <div className="flex gap-3">
          <button
            onClick={unlockSelectedBundle}
            className="flex-1 py-2 bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg font-medium hover:opacity-90 transition-colors"
          >
            Unlock
          </button>
          <button
            onClick={() => {
              setShowBundlePasswordModal(false);
              setBundlePassword('');
            }}
            className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default TokenCreationForm;